import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { ApiError } from '@/api/errors'
import { cn } from '@/lib/cn'

/**
 * Toast — feedback de ação.
 *
 * Substitui as 7 implementações diferentes do sistema antigo e, mais importante,
 * cobre o **B-12**: as telas React não tinham toast nenhum — erro ia só para o
 * `console.error` e o usuário clicava sem saber se deu certo.
 *
 * Regras:
 *  - sucesso desaparece sozinho; **erro fica até ser fechado** (o usuário precisa
 *    ler o motivo, e erro do SISCOMEX costuma ter código relevante);
 *  - aceita `ApiError` direto e extrai a mensagem em PT-BR + o código do Portal;
 *  - `aria-live` para leitor de tela.
 */

type Tom = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  tom: Tom
  titulo: string
  detalhe?: string
  codigo?: string | null
  /** ms; 0 = não fecha sozinho. */
  duracao: number
}

interface ToastAPI {
  sucesso: (titulo: string, detalhe?: string) => void
  erro: (titulo: string | unknown, detalhe?: string) => void
  aviso: (titulo: string, detalhe?: string) => void
  info: (titulo: string, detalhe?: string) => void
}

const Ctx = createContext<ToastAPI | null>(null)

export function useToast(): ToastAPI {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useToast precisa estar dentro de <ToastProvider>')
  return ctx
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [lista, setLista] = useState<Toast[]>([])

  const remover = useCallback((id: string) => {
    setLista((l) => l.filter((t) => t.id !== id))
  }, [])

  const adicionar = useCallback((t: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID()
    setLista((l) => [...l, { ...t, id }])
  }, [])

  const api = useMemo<ToastAPI>(
    () => ({
      sucesso: (titulo, detalhe) =>
        adicionar({ tom: 'success', titulo, detalhe, duracao: 4000 }),
      aviso: (titulo, detalhe) => adicionar({ tom: 'warning', titulo, detalhe, duracao: 6000 }),
      info: (titulo, detalhe) => adicionar({ tom: 'info', titulo, detalhe, duracao: 4000 }),
      erro: (titulo, detalhe) => {
        // Aceita ApiError, Error ou string — sempre resulta em texto legível.
        if (titulo instanceof ApiError) {
          adicionar({
            tom: 'error',
            titulo: titulo.message,
            detalhe: detalhe,
            codigo: titulo.siscomexCode,
            duracao: 0, // erro não desaparece sozinho
          })
          return
        }
        if (titulo instanceof Error) {
          adicionar({ tom: 'error', titulo: titulo.message, detalhe, duracao: 0 })
          return
        }
        adicionar({ tom: 'error', titulo: String(titulo), detalhe, duracao: 0 })
      },
    }),
    [adicionar],
  )

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div
          // `polite` para não interromper o que o leitor de tela está falando.
          aria-live="polite"
          aria-atomic="false"
          className="app-caixa-alta pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2"
        >
          {lista.map((t) => (
            <ToastItem key={t.id} toast={t} onFechar={() => remover(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  )
}

/* -------------------------------------------------------------------------- */

const ESTILO: Record<Tom, { caixa: string; icone: ReactNode }> = {
  success: {
    caixa: 'bg-white ring-success-200 border-l-success-600',
    icone: (
      <svg viewBox="0 0 20 20" className="size-4 text-success-600" fill="none" aria-hidden>
        <path d="M5 10.5l3.5 3.5L15 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  error: {
    caixa: 'bg-white ring-danger-200 border-l-danger-600',
    icone: (
      <svg viewBox="0 0 20 20" className="size-4 text-danger-600" fill="none" aria-hidden>
        <path d="M10 6.5v4.5m0 3h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  warning: {
    caixa: 'bg-white ring-warning-200 border-l-warning-600',
    icone: (
      <svg viewBox="0 0 20 20" className="size-4 text-warning-600" fill="none" aria-hidden>
        <path d="M10 7.5v3.5m0 2.5h.01M10 3L2.5 16.5h15L10 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  info: {
    caixa: 'bg-white ring-brand-200 border-l-brand-600',
    icone: (
      <svg viewBox="0 0 20 20" className="size-4 text-brand-600" fill="none" aria-hidden>
        <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M10 9v4.5M10 6.5h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
}

function ToastItem({ toast, onFechar }: { toast: Toast; onFechar: () => void }) {
  const { tom, titulo, detalhe, codigo, duracao } = toast

  useEffect(() => {
    if (!duracao) return
    const t = setTimeout(onFechar, duracao)
    return () => clearTimeout(t)
  }, [duracao, onFechar])

  const e = ESTILO[tom]

  return (
    <div
      role={tom === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex gap-2.5 rounded-md border-l-2 px-3 py-2.5 shadow-md ring-1',
        e.caixa,
      )}
    >
      <span className="mt-0.5 shrink-0">{e.icone}</span>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink-800">{titulo}</p>
        {detalhe && <p className="mt-0.5 text-xs text-ink-600">{detalhe}</p>}
        {codigo && (
          <p className="mt-0.5 font-mono text-2xs text-ink-500">Código SISCOMEX: {codigo}</p>
        )}
      </div>

      <button
        type="button"
        onClick={onFechar}
        aria-label="Fechar aviso"
        className="-mt-0.5 -mr-1 shrink-0 self-start rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-600"
      >
        <svg viewBox="0 0 20 20" className="size-3.5" fill="none" aria-hidden>
          <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  )
}
